import type { TFunction } from 'i18next';
import i18n from 'i18next';

import { AlertSeverityEnum } from '../../enums/AlertSeverityEnum';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import type ActiveAlertItem from '../../interfaces/ActiveAlertItem';

const SEVERITY_ORDER: Record<string, number> = {
  [AlertSeverityEnum.Critical]: 0,
  [AlertSeverityEnum.Warning]: 1,
  [AlertSeverityEnum.Info]: 2,
};

/**
 * Maps API severity string to a known enum value; unknown → warning.
 */
export function normalizeAlertSeverity(severity: string): AlertSeverityEnum {
  const lower = severity?.toLowerCase?.() ?? '';
  if (lower === AlertSeverityEnum.Critical) {
    return AlertSeverityEnum.Critical;
  }
  if (lower === AlertSeverityEnum.Warning) {
    return AlertSeverityEnum.Warning;
  }
  if (lower === AlertSeverityEnum.Info) {
    return AlertSeverityEnum.Info;
  }
  return AlertSeverityEnum.Warning;
}

function severityRank(severity: string): number {
  const key = normalizeAlertSeverity(severity);
  return SEVERITY_ORDER[key] ?? 99;
}

/**
 * critical > warning > info; same rank keeps API order (stable sort).
 */
export function sortActiveAlerts(alerts: ActiveAlertItem[]): ActiveAlertItem[] {
  return [...alerts]
    .map((a, index) => ({ a, index }))
    .sort((x, y) => {
      const dr = severityRank(x.a.severity as string) - severityRank(y.a.severity as string);
      if (dr !== 0) {
        return dr;
      }
      return x.index - y.index;
    })
    .map(({ a }) => ({
      ...a,
      severity: normalizeAlertSeverity(a.severity as string),
    }));
}

/**
 * Metadata passed to i18n; only safe primitives for known keys.
 */
export function metadataForI18n(
  metadata: Record<string, unknown> | null,
): Record<string, string | number | boolean> {
  if (!metadata) {
    return {};
  }
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(metadata)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Resolved message for an alert: known code keys, then generic fallback.
 */
export function getAlertMessage(alert: ActiveAlertItem, t: TFunction): string {
  const meta = metadataForI18n(alert.metadata);
  const specificKey = `alerts.codes.${alert.code}.message`;
  if (i18n.exists(specificKey)) {
    return t(specificKey, { ...meta });
  }
  return t('alerts.codes._generic.message', { code: alert.code, ...meta });
}

export function alertSupportsTotpCta(code: string): boolean {
  return code === ApiErrorEnum.TotpNotEnabled || code === ApiErrorEnum.LowBackupCodes;
}

/**
 * True when the "Dismiss" action must be hidden for this alert: the `TOTP_NOT_ENABLED` alert
 * once the company's `TOTP_REQUIRED` policy grace period has elapsed (mandatory enrollment) — the
 * user must use "Manage 2FA" instead of snoozing/dismissing the banner.
 */
export function isAlertDismissBlockedByMandatoryTotp(code: string, isMandatory: boolean): boolean {
  return isMandatory && code === ApiErrorEnum.TotpNotEnabled;
}

/**
 * Maps normalized severity to Ant Design Alert `type`.
 */
export function alertSeverityToAntdType(
  severity: string,
): 'error' | 'info' | 'success' | 'warning' {
  const s = normalizeAlertSeverity(severity);
  if (s === AlertSeverityEnum.Critical) {
    return 'error';
  }
  if (s === AlertSeverityEnum.Warning) {
    return 'warning';
  }
  return 'info';
}

const SNOOZE_PRESET_KEY = (hours: number): string => `common.alerts.snoozePreset.h${hours}`;

export function formatSnoozeHoursLabel(hours: number, t: TFunction): string {
  const key = SNOOZE_PRESET_KEY(hours);
  if (i18n.exists(key)) {
    return t(key);
  }
  return t('common.alerts.snoozeHoursGeneric', { count: hours });
}
